/**
 * Whether a query string is an Auth0 callback, by Auth0's own rule
 * (`hasAuthParams` in @auth0/auth0-react, which it doesn't export): a
 * non-empty `code` (or `connect_code`) or `error`, plus a non-empty `state`.
 * Only then does the SDK process the URL and, when done, navigate or report
 * an error.
 */
export function isAuth0Callback(search: string): boolean {
  return (
    (/[?&](?:connect_)?code=[^&]+/.test(search) || /[?&]error=[^&]+/.test(search)) &&
    /[?&]state=[^&]+/.test(search)
  );
}

/** Query parameters Auth0 adds on its way back. */
const AUTH0_CALLBACK_PARAMS = ["code", "connect_code", "state", "error", "error_description"];

/**
 * The page to come back to after sign-in: the current path, query and hash.
 * When the URL is an Auth0 callback, its parameters are dropped: left in, a
 * stale `?code=&state=` would come back and fail as "Invalid state". A page's
 * own `state` or `error` parameters, on a URL that isn't a callback, are kept.
 */
export function returnPathFrom(location: {
  pathname: string;
  search: string;
  hash: string;
}): string {
  if (!isAuth0Callback(location.search)) {
    return location.pathname + location.search + location.hash;
  }
  const params = new URLSearchParams(location.search);
  for (const name of AUTH0_CALLBACK_PARAMS) params.delete(name);
  const query = params.toString();
  return location.pathname + (query ? `?${query}` : "") + location.hash;
}
